/**
 * AppsPage — list of deployed managed-app instances for a project.
 *
 * Mounted at /projects/:projectId/apps as a child of ProjectLayout (so
 * the sidebar / header / project context come from the layout). Just
 * the list content lives here.
 *
 * Data: GET /apps?projectId=<id> returns the project's deployed app
 * rows newest-first. Click a row → /projects/:projectId/apps/:instanceId.
 *
 * Visual language is a deliberate clone of PipelinesPage:
 *   - same `pipelines-page` wrapper + `pipelines-header*` chrome
 *   - same shared DataTable atom with column class names from the
 *     pipelines stylesheet
 *   - same dark theme + no new colors
 * Deploying a new app goes through the marketplace, same as deploying
 * a new pipeline goes through the workflow marketplace.
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DataTable from '../../components/shared/DataTable/DataTable';
import AppStatus from './AppStatus';
import AppsDemo from '../../components/shared/GetStartedSteps/AppsDemo';
import McpDemo from '../../components/shared/GetStartedSteps/McpDemo';
import DemoCarousel from '../../components/shared/GetStartedSteps/DemoCarousel';
import { Apps } from '../../services/apps';
import { useApps, useDestroyApp, useDestroySoloApp } from '../../hooks/useApps';
import { usePinnedItems } from '../../hooks/usePinnedItems';
import { appPinId } from '../../utils/pinIds';
import { timeAgo } from '../../utils/timeAgo';
// Reuse the pipelines stylesheet for header / wrapper / column widths so
// the two list pages look like siblings.
import { projectAppsMarketplacePath } from '../../utils/marketplacePaths';
import SoloDestroyModal from '../../components/shared/SoloDestroyModal/SoloDestroyModal';
import LetterAvatar from '../../components/atoms/LetterAvatar/LetterAvatar';
import '../PipelinesPage/PipelinesPage.css';

// App logo in the NAME cell — mirrors PipelinesPage's WorkflowIcon. The
// deployed-instance list API does NOT carry the catalog icon, so AppsPage
// fetches the (public) catalog once and maps appType → icon URL. On a
// missing/flaky icon we fall back to the shared LetterAvatar placeholder
// instead of a broken image.
const AppIcon = ({ iconUrl, label }) => {
  const [failed, setFailed] = useState(false);
  if (!iconUrl || failed) {
    return <LetterAvatar name={label} />;
  }
  return (
    <img
      src={iconUrl}
      onError={() => setFailed(true)}
      className="pipelines-name-icon"
      alt=""
      loading="lazy"
    />
  );
};

const AppsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};

  // Apps list — React-Query cached (5min staleTime). The two-source merge
  // (project cloud + account-scoped solo) + terminated-solo retention all
  // live inside the useApps queryFn now. Navigating away and back within the
  // staleTime window reads from cache instead of refetching.
  const { data: apps = [], isLoading: loading, error: appsError } = useApps(projectId);

  // Delete mutations — on success they invalidate the project's app LIST (so
  // the destroyed/terminated row updates in place, no skeleton flash) AND
  // remove the app's cached DETAIL (no ghost). Cloud + solo have separate
  // teardown paths, so separate hooks.
  const destroyApp = useDestroyApp(projectId);
  const destroySoloApp = useDestroySoloApp(projectId);

  // Sidebar pin state (localStorage, per-project; default = pinned). Shared
  // with the sidebar via usePinnedItems — toggling here updates it live.
  const { isPinned, togglePin } = usePinnedItems(projectId);

  // appType → catalog icon URL, fetched once from the public catalog so
  // deployed rows can show the app's logo (the instance list omits it).
  const [iconByType, setIconByType] = useState({});
  // App delete confirmation: holds the app row pending a styled-modal
  // destroy (null = modal closed). Both solo (type-to-confirm, data loss)
  // and cloud (plain styled confirm) flow through the same modal — the row's
  // deployMode decides which variant renders. Destroy is fire-and-forget: the
  // modal closes on confirm and the teardown runs in the background, so there's
  // no in-flight "busy" state to gate.
  const [destroyTarget, setDestroyTarget] = useState(null);

  // Surface a load failure as a toast (the old fetch loop did this in its
  // catch). React Query owns retries/caching; we only mirror the toast.
  useEffect(() => {
    if (appsError) {
      setToast?.({ isOpen: true, message: `Could not load apps: ${appsError.message}`, color: 'danger' });
    }
  }, [appsError, setToast]);

  // Catalog icons — only when there ARE deployed apps whose rows need an icon.
  // Fetching the (heavy) marketplace catalog on an EMPTY apps list is pure waste:
  // the catalog belongs to the Marketplace surface, not "my deployed apps". Gate
  // on the instance count so an empty list fires zero catalog requests; re-runs if
  // apps appear. Non-fatal — a miss just falls back to the LetterAvatar.
  const deployedAppCount = apps.length;
  useEffect(() => {
    if (deployedAppCount === 0) return undefined;
    let alive = true;
    Apps.listCatalog()
      .then((data) => {
        if (!alive) return;
        const map = {};
        for (const c of (data?.items || [])) {
          if (c.appType) map[c.appType] = c.icon || null;
        }
        setIconByType(map);
      })
      .catch((e) => console.warn('apps catalog icons unavailable:', e?.message));
    return () => { alive = false; };
  }, [deployedAppCount]);

  // ── solo helpers ─────────────────────────────────────────────────────
  // Solo apps (deployMode==='solo') run on a dedicated EC2, are keyed by
  // appSlug (not instanceId), expose a public hostname (`domain`), and
  // MUST be torn down via DELETE /apps/solo/{slug} so the VM + Elastic IP
  // + Route53 record are all cleaned up. Cloud apps are unchanged.
  const isSolo = (a) => a?.deployMode === 'solo';
  // Public URL: solo prefers its trusted https domain, falling back to
  // the raw public IP (http, pre-DNS). Cloud keeps url/subdomain.
  const urlFor = (a) => {
    if (isSolo(a)) {
      if (a.domain) return `https://${a.domain}`;
      if (a.publicIp) return `http://${a.publicIp}`;
      return null;
    }
    return a.url || (a.subdomain ? `https://${a.subdomain}` : null);
  };
  // Solo status for the pill: prefer the lifecycle `status`
  // (active/paused/terminated), but while provisioning surface the
  // finer-grained `phase` (provisioning/running/failed).
  const statusFor = (a) => {
    if (isSolo(a)) {
      if (a.status === 'active') return a.phase || a.status;
      return a.status || a.phase;
    }
    return a.status;
  };

  // ── columns ────────────────────────────────────────────────────────
  // Mirror the Pipelines columns: NAME / STATUS / VERSION-equivalent /
  // LAST DEPLOYED. The app-specific "VERSION" column is the app's
  // catalog version (n8n v1.97.1 etc) since users care about that more
  // than the instance row's internal version number.
  const columns = [
    {
      header: 'NAME',
      key: 'name',
      className: 'pipelines-col-name',
      skeleton: 'avatar',
      render: (a) => {
        const label = a.name || a.appType || a.appSlug || a.instanceId;
        return (
          <span className="pipelines-name-cell">
            <AppIcon iconUrl={iconByType[a.appType] || iconByType[a.appSlug]} label={label} />
            <span className="pipelines-name-text">
              <span className="pipelines-name">{label}</span>
            </span>
            {/* Only flag dedicated (solo) apps — "Cloud" is the default and
                the badge added no signal, just noise. */}
            {isSolo(a) && (
              <span className="apps-deploy-badge" data-mode="solo">Dedicated</span>
            )}
          </span>
        );
      },
    },
    {
      header: 'STATUS',
      key: 'status',
      className: 'pipelines-col-status',
      skeleton: 'status',
      render: (a) => (
        <AppStatus
          status={statusFor(a)}
          runningCount={a.runningCount}
          desiredCount={a.desiredCount}
          variant="table"
        />
      ),
    },
    {
      header: 'APP',
      key: 'appType',
      className: 'pipelines-col-version',
      render: (a) => (
        <span className="pipelines-version">
          {isSolo(a)
            ? `${a.appSlug}${a.tier ? ` · ${a.tier}` : ''}`
            : `${a.appType || ''}${a.appVersion ? ` v${a.appVersion}` : ''}`}
        </span>
      ),
    },
    {
      header: 'CREATED',
      key: 'createdAt',
      className: 'pipelines-col-deployed',
      render: (a) => (
        <span className="pipelines-deployed">
          {a.createdAt ? timeAgo(a.createdAt) : '—'}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: 'View details',
      // Cloud apps have an instanceId-keyed detail page; solo apps have a
      // slug-keyed one (/apps/solo/:slug). Both live under the current
      // project route so the layout/sidebar context comes along.
      onClick: (a) => {
        if (isSolo(a)) {
          navigate(`/projects/${projectId}/apps/solo/${a.appSlug}`);
          return;
        }
        navigate(`/projects/${projectId}/apps/${a.instanceId}`);
      },
    },
    {
      label: 'Open app',
      onClick: (a) => {
        const url = urlFor(a);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      },
    },
    {
      // Sidebar pin toggle — label flips with the row's current state.
      // Stored per-project in localStorage; default is pinned.
      label: (a) => (isPinned(appPinId(a)) ? 'Unpin' : 'Pin'),
      onClick: (a) => togglePin(appPinId(a)),
    },
    { divider: true },
    {
      label: 'Destroy',
      danger: true,
      // Both solo and cloud teardown open the styled DestroyAppModal rather
      // than a bare confirm(). The modal renders the type-to-confirm /
      // data-loss variant for solo (deployMode==='solo') and a plain styled
      // confirm for cloud — see the SoloDestroyModal render below. The
      // actual DELETE runs in confirmDestroy once the user confirms.
      onClick: (a) => { setDestroyTarget(a); },
    },
  ];

  const confirmDestroy = () => {
    const a = destroyTarget;
    if (!a) return;
    const name = a.name || a.appSlug || a.instanceId;
    // FIRE-AND-FORGET: close the modal immediately and let the teardown run in
    // the background. The backend destroy is synchronous + slow (tears the
    // Fargate/VM resources DOWN before returning), so awaiting it here froze the
    // modal for the whole teardown. The mutation's onMutate already drops the
    // row from the list + sidebar optimistically, so the UI is instant; onError
    // rolls those back and we surface a toast if the teardown actually failed.
    const onError = (err) =>
      setToast?.({ isOpen: true, message: `Destroy failed: ${err?.message || err}`, color: 'danger' });
    setDestroyTarget(null);
    if (isSolo(a)) {
      // SOLO teardown — DELETE /apps/solo/{slug}. Terminates the VM, releases
      // its EIP, removes the Route53 record, deletes the EBS volume + S3
      // (Litestream) backup. The row is KEPT (terminated-in-place) so its
      // CloudWatch logs, retained 14d, stay reachable from the detail page.
      destroySoloApp.mutate(a.appSlug, { onError });
      setToast?.({ isOpen: true, message: `Deleting "${name}"… VM + EBS + S3 removed; logs kept 14d`, color: 'success' });
    } else {
      // CLOUD teardown — DELETE /apps/{instanceId}. Stops the Fargate task and
      // removes the service. onMutate already dropped the row; onSettled
      // reconciles once destroyedAt is stamped server-side.
      destroyApp.mutate(a.instanceId, { onError });
      setToast?.({ isOpen: true, message: `Destroying "${name}"…`, color: 'success' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Apps · Teamy</title>
      </Helmet>

      <SoloDestroyModal
        open={!!destroyTarget}
        requireType={!!destroyTarget && isSolo(destroyTarget)}
        label={destroyTarget
          ? (isSolo(destroyTarget)
            ? destroyTarget.appSlug
            : (destroyTarget.name || destroyTarget.appType || destroyTarget.instanceId))
          : ''}
        confirmWord={destroyTarget?.appSlug}
        busy={false}
        onClose={() => setDestroyTarget(null)}
        onConfirm={confirmDestroy}
      />

      {/* Empty state matches Pipelines exactly — the AppsDemo terminal
          mockup sits in a .pipelines-empty wrapper, no outer page
          padding. WorkflowDemo / AppsDemo handle their own chrome. */}
      {!loading && apps.length === 0 ? (
        <div className="pipelines-empty">
          <DemoCarousel slides={[() => <McpDemo context="apps" />, () => <AppsDemo />]} />
        </div>
      ) : (
        <div className="pipelines-page">
          {/* Header shows during loading too so the skeleton keeps title +
              subtitle — no layout shift when rows land. Empty state is above. */}
          {(loading || apps.length > 0) && (
            <div className="pipelines-header">
              <div className="pipelines-header-row">
                <h1 className="pipelines-title">Apps</h1>
              </div>
              <p className="pipelines-subtitle">
                Managed app instances deployed in this project.
                {' '}Deploy a new one from the{' '}
                <Link to={projectAppsMarketplacePath(projectId)} className="pipelines-subtitle-link">
                  app marketplace
                </Link>.
              </p>
            </div>
          )}

          <DataTable
            data={apps}
            columns={columns}
            loading={loading}
            rowKey="instanceId"
            actions={actions}
            onRowClick={(a) => {
              // Solo rows open their slug-keyed detail page; cloud rows open
              // the instanceId-keyed one. Both stay under the project route.
              if (isSolo(a)) {
                navigate(`/projects/${projectId}/apps/solo/${a.appSlug}`);
                return;
              }
              navigate(`/projects/${projectId}/apps/${a.instanceId}`);
            }}
          />
        </div>
      )}
    </>
  );
};

export default AppsPage;
